import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Fingerprint, Lock, CheckCircle2, ShieldX } from "lucide-react";
import { api } from "@/context/AuthContext";
import { requestDeviceBiometrics } from "@/components/register/PasskeySetup";

const DEMO_LOC = { lat: 40.4168, lng: -3.7038 };

const getLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, maximumAge: 60000 }
    );
  });

export const GoLiveModal = ({ t, open, onClose, onResult }) => {
  const [state, setState] = useState("idle");

  const authenticate = async () => {
    setState("checking");
    let biometricOk = false;
    try {
      biometricOk = await requestDeviceBiometrics();
    } catch (e) {
      biometricOk = false;
    }
    if (!biometricOk) {
      try { await api.post("/avatar/go-live", { biometric_ok: false }); } catch (_) {}
      setState("failed");
      setTimeout(() => { onResult("Offline"); setState("idle"); onClose(); }, 2200);
      return;
    }
    let loc = await getLocation();
    if (!loc) {
      loc = DEMO_LOC;
      toast.info(t.geoDenied);
    }
    try {
      const { data } = await api.post("/avatar/go-live", { biometric_ok: true, ...loc });
      setState("success");
      setTimeout(() => { onResult(data.avatar_status); setState("idle"); onClose(); }, 1400);
    } catch (e) {
      setState("failed");
      setTimeout(() => { onResult("Offline"); setState("idle"); onClose(); }, 2200);
    }
  };

  const cancel = async () => {
    if (state === "checking") return;
    if (state === "idle") {
      try { await api.post("/avatar/go-live", { biometric_ok: false }); } catch (_) {}
      onResult("Offline");
    }
    setState("idle");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#05050A]/60 backdrop-blur-xl px-6"
          data-testid="security-lock-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-md bg-[#0F0F1A]/90 border border-white/10 rounded-3xl p-8 text-center"
          >
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#B026FF]/15 border border-[#B026FF]/40 flex items-center justify-center mb-5">
              <Lock size={26} className="text-[#D080FF]" />
            </div>
            <h2 className="font-heading text-lg font-black text-white mb-2">{t.lockTitle}</h2>
            <p className="text-sm text-[#A0A0B0] mb-8">{t.lockSub}</p>

            {state === "success" ? (
              <motion.p initial={{ scale: 0.8 }} animate={{ scale: 1 }} data-testid="golive-success-msg"
                className="text-[#00FFAA] font-bold text-sm flex items-center justify-center gap-2 py-3">
                <CheckCircle2 size={18} /> {t.dispatched}
              </motion.p>
            ) : state === "failed" ? (
              <p data-testid="golive-failed-msg" className="text-red-400 font-bold text-sm flex items-center justify-center gap-2 py-3">
                <ShieldX size={18} /> {t.restricted}
              </p>
            ) : (
              <>
                <button data-testid="biometric-authenticate-btn" onClick={authenticate} disabled={state === "checking"}
                  className="w-full py-4 rounded-full font-bold text-xs uppercase tracking-widest bg-[#B026FF] shadow-[0_0_20px_rgba(176,38,255,0.45)] flex items-center justify-center gap-2 disabled:opacity-70">
                  <Fingerprint size={18} className={state === "checking" ? "animate-pulse" : ""} />
                  {state === "checking" ? t.authenticating : t.authenticate}
                </button>
                <button data-testid="golive-cancel-btn" onClick={cancel}
                  className="mt-4 text-xs text-white/40 hover:text-white/70 uppercase tracking-widest font-bold">
                  {t.cancel}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
